// =============================================================================
// echelleTexte.js : l'échelle que la taille du texte applique RÉELLEMENT à la
// mise en page (réglage « Taille du texte » de la feuille « Moi »), une seule
// source pour le CSS et pour le JavaScript (01/10/2026).
//
// La taille du texte est un `zoom` posé sur <body> (appShared.applyFontSize) :
// Grand 1,18, Très grand 1,38. Sous ce zoom, Safari, Chrome et Firefox
// multiplient les unités d'écran (vh, dvh, vw) ET les déplacements écrits en
// px depuis le doigt : une feuille de 100dvh rend 1,38 écran, une feuille
// qu'on fait glisser de 100 px de doigt part de 138 px. Le défaut s'est vu sur
// l'iPhone de Pierre le 01/10 : la feuille « Moi » perdait poignée, titre et
// croix au-dessus de l'écran, en Grand comme en Très grand.
//
// Remède d'ensemble :
//   CSS  la variable --fs-zoom porte cette échelle ; les jetons --vh100 et
//        --vw100 (src/theme.css) la divisent. Toute géométrie d'écran passe
//        par eux (verrou : src/echelleTexte.test.js).
//   JS   echelleTexte() rend la même valeur ; un geste qui pose un
//        translate depuis clientX/Y la divise (FeuilleNavigation, fenetreGeste).
//
// --fs-zoom vaut 1 quand le navigateur ne connaît pas `zoom` (Firefox avant
// 126) : rien n'y est agrandi, rien ne doit y être divisé.
// =============================================================================

export const VAR_ECHELLE_TEXTE = "--fs-zoom";

/** Taille de police CALCULÉE en px (nuage de mots, pastille à la taille de son
 *  conteneur…) : multipliée par --fs-texte, comme un jeton --fs-*, pour que
 *  l'iPad l'agrandisse (mode « jetons », plus bas). 1 partout ailleurs. Une
 *  taille fixe s'écrit en jeton (var(--fs-meta)…) ou en
 *  "calc(<n>px * var(--fs-texte, 1))" (verrou : src/echelleTexte.test.js). */
export const taillePx = (n) => `calc(${n}px * var(--fs-texte, 1))`;

/** Le navigateur applique-t-il la propriété CSS `zoom` ? */
export function zoomCssDisponible() {
  try {
    return typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("zoom", "2");
  } catch { return false; }
}

/** Pose l'échelle rendue sur <html> (appelée par applyFontSize). */
export function poserEchelleTexte(echelle) {
  if (typeof document === "undefined") return 1;
  const e = Number.isFinite(echelle) && echelle > 0 ? echelle : 1;
  document.documentElement.style.setProperty(VAR_ECHELLE_TEXTE, String(e));
  return e;
}

/** Échelle rendue de la taille du texte (1 en taille normale). */
export function echelleTexte() {
  if (typeof document === "undefined") return 1;
  const v = parseFloat(document.documentElement.style.getPropertyValue(VAR_ECHELLE_TEXTE));
  return Number.isFinite(v) && v > 0 ? v : 1;
}

// =============================================================================
// Safari iOS et iPadOS : le texte ne suivait pas la taille du texte (relecture
// du 01/10/2026, Safari 26.5, mesuré au simulateur). Le zoom de <body> agrandit
// boîtes, icônes et marges, mais WebKit recalcule la police d'après sa taille
// DÉCLARÉE, sans le zoom, et pour deux raisons différentes :
//   iPhone  html{-webkit-text-size-adjust:100%} (posé pour que le texte ne
//           grossisse pas tout seul en paysage) rend « déclarée x 100 % ».
//           auto répare, mais regrossit les paragraphes en paysage (15 px
//           rendus 25 px) : exclu. « échelle x 100 % » répare exactement, sans
//           rien en paysage : mode « pourcentage » (App.jsx, coquilleStyles.js).
//   iPad    l'ajustement « idempotent » ramène toute police porteuse de texte
//           à sa taille déclarée, quel que soit text-size-adjust (auto, none,
//           100 % ou pourcentage : rien n'y fait). Seule une taille DÉCLARÉE
//           plus grande passe : les jetons --fs-* sont multipliés par
//           --fs-texte (theme.css) : mode « jetons ».
// Dans les deux modes, WebKit applique alors l'échelle DEUX fois aux longueurs
// en em ou ch (largeur, hauteur, marge, interligne en em, letter-spacing) :
// --em-ajuste (theme.css) vaut 1 / échelle et les géométries en em s'écrivent
// calc(<n>em * var(--em-ajuste, 1)) (verrou : src/echelleTexte.test.js).
//
// Pas de détection de navigateur : une sonde MESURE la hauteur rendue d'une
// ligne de texte (interligne 1) contre une boîte en px sous le même zoom, et
// essaie les deux remèdes. Chrome, Firefox, Safari du Mac et toute version de
// WebKit corrigée gardent la ligne à la hauteur de la boîte : rien n'est posé.
// =============================================================================

export const ATTR_TEXTE_AJUSTE = "data-texte-ajuste";

/** Pourcentage de text-size-adjust qui rend l'échelle `echelle`. */
export const pourcentageAjustement = (echelle) => `${Math.round(echelle * 1000) / 10}%`;

const proche = (a, b) => Math.abs(a / b - 1) < 0.03;

/**
 * Décide d'après des hauteurs rendues sous le zoom de <body> : `boite` (50 px
 * déclarés, que tout moteur agrandit), `cent` (une ligne de 50 px sous 100 %),
 * `pourcent` (la même sous « échelle x 100 % »), `jetons` (une ligne déclarée
 * à 50 px x échelle, sous 100 %). Rend null (le texte suit déjà le zoom, ou
 * mesure illisible : on ne touche à rien), "pourcentage" ou "jetons".
 */
export function decisionAjustement(m, echelle) {
  const { boite, cent, pourcent, jetons } = m || {};
  if (!(echelle > 1.001) || !(boite > 0) || !(cent > 0)) return null;
  if (!proche(cent * echelle, boite)) return null;   // le texte suit le zoom (ou mesure étrangère)
  if (pourcent > 0 && proche(pourcent, boite)) return "pourcentage";
  if (jetons > 0 && proche(jetons, boite)) return "jetons";
  return null;
}

const CORPS_SONDE = 50;

/** Mesure les hauteurs (une mise en page synchrone, au seul changement de taille). */
export function sonderAjustement(echelle) {
  if (typeof document === "undefined" || !document.body) return null;
  const boite = document.createElement("div");
  boite.setAttribute("aria-hidden", "true");
  boite.style.cssText = "position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;white-space:nowrap;";
  const el = (css, texte) => {
    const s = document.createElement("div");
    if (texte) s.textContent = "Aramis";
    s.style.cssText = `display:inline-block;vertical-align:top;line-height:1;${css}`;
    boite.appendChild(s);
    return s;
  };
  // Une sonde de MESURE, hors de l'échelle typographique : une taille en px
  // brute, jamais un jeton --fs-* (que le mode « jetons » multiplie).
  const corps = (px) => `font-size:${Math.round(px * 100) / 100}px;`;
  const tsa = (v) => `-webkit-text-size-adjust:${v};text-size-adjust:${v};`;
  const b = el(`width:4px;height:${CORPS_SONDE}px;`, false);
  const c = el(corps(CORPS_SONDE) + tsa("100%"), true);
  const p = el(corps(CORPS_SONDE) + tsa(pourcentageAjustement(echelle)), true);
  const j = el(corps(CORPS_SONDE * echelle) + tsa("100%"), true);
  document.body.appendChild(boite);
  try {
    const h = (x) => x.getBoundingClientRect().height;
    return { boite: h(b), cent: h(c), pourcent: h(p), jetons: h(j) };
  } catch { return null; } finally { boite.remove(); }
}

/** Pose ou retire data-texte-ajuste selon la sonde (appelée par applyFontSize). */
export function poserAjustementTexte(echelle) {
  if (typeof document === "undefined") return null;
  const html = document.documentElement;
  const d = echelle > 1.001 ? decisionAjustement(sonderAjustement(echelle), echelle) : null;
  if (d) html.setAttribute(ATTR_TEXTE_AJUSTE, d);
  else html.removeAttribute(ATTR_TEXTE_AJUSTE);
  return d;
}
